import { useState } from "react";
import { X, Minus, Plus, Check } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerClose,
} from "./ui/drawer";
import { useCart } from "../context/CartContext";

const ProductBottomSheet = ({ product, isOpen, onClose, eventEndTime }) => {
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const { addItem } = useCart();

  if (!product) return null;

  const isExpired = eventEndTime ? new Date(eventEndTime) <= new Date() : false;
  const sizeRequired = product.sizes && product.sizes.length > 1;
  const sizeMissing = sizeRequired && !selectedSize;

  const handleAdd = () => {
    if (isExpired || sizeMissing) return;
    addItem(product, { size: selectedSize || (product.sizes?.[0] ?? null), quantity });
    setAdded(true);
    setTimeout(() => {
      setAdded(false);
      onClose();
      setQuantity(1);
      setSelectedSize(null);
    }, 900);
  };

  const incrementQuantity = () => setQuantity((q) => Math.min(q + 1, 10));
  const decrementQuantity = () => setQuantity((q) => Math.max(q - 1, 1));

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="bg-white border-0 rounded-t-3xl max-h-[85vh]" data-testid="product-bottom-sheet">
        <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-gray-200 mt-4" />
        
        <DrawerHeader className="px-6 pt-4 pb-2">
          <div className="flex justify-between items-start">
            <div className="flex-1 pr-4">
              <p className="text-gray-500 text-xs font-serif uppercase tracking-[0.15em] mb-1">
                {product.brand}
              </p>
              <DrawerTitle className="text-black text-lg font-sans font-semibold leading-tight text-left">
                {product.name}
              </DrawerTitle>
            </div>
            <DrawerClose asChild>
              <button
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100"
                data-testid="close-sheet-button"
              >
                <X size={18} className="text-gray-600" />
              </button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        <div className="px-6 pb-8 overflow-y-auto">
          {/* Description */}
          <DrawerDescription className="text-gray-600 text-sm font-sans leading-relaxed mb-6 text-left">
            {product.description}
          </DrawerDescription>

          {/* Sizes */}
          <div className="mb-6">
            <h4 className="text-black text-sm font-sans font-medium mb-3">Размеры</h4>
            <div className="flex gap-2 flex-wrap" data-testid="size-selector">
              {product.sizes.map((size) => (
                <button
                  key={size}
                  onClick={() => setSelectedSize(size)}
                  className={`min-w-[48px] h-10 px-4 rounded-full border text-sm font-sans font-medium transition-all ${
                    selectedSize === size
                      ? "bg-black text-white border-black"
                      : "bg-white text-black border-gray-200 hover:border-gray-400"
                  }`}
                  data-testid={`size-${size}`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Quantity */}
          <div className="mb-6">
            <h4 className="text-black text-sm font-sans font-medium mb-3">Кол-во</h4>
            <div className="flex items-center gap-4" data-testid="quantity-selector">
              <button
                onClick={decrementQuantity}
                className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 hover:border-gray-400 transition-colors"
                data-testid="quantity-minus"
              >
                <Minus size={16} className="text-gray-600" />
              </button>
              <span className="text-black text-lg font-sans font-medium w-8 text-center" data-testid="quantity-value">
                {quantity}
              </span>
              <button
                onClick={incrementQuantity}
                className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 hover:border-gray-400 transition-colors"
                data-testid="quantity-plus"
              >
                <Plus size={16} className="text-gray-600" />
              </button>
            </div>
          </div>

          {/* Price block */}
          <div className="mb-6 p-4 bg-gray-50 rounded-2xl">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-500 text-xs font-sans mb-1">Розничная цена</p>
                <p className="text-gray-400 text-sm font-sans line-through">
                  {product.retailPrice.toFixed(2)} ₽
                </p>
              </div>
              <div className="text-right">
                <p className="text-gray-500 text-xs font-sans mb-1">Ваша цена</p>
                <p className="text-black text-xl font-sans font-semibold" data-testid="sheet-sale-price">
                  {(product.salePrice * quantity).toFixed(2)} ₽
                </p>
              </div>
            </div>
          </div>

          {/* Add to cart button */}
          <button
            onClick={handleAdd}
            disabled={isExpired || sizeMissing}
            className={`w-full font-semibold py-4 rounded-full flex items-center justify-center gap-2 font-sans text-sm tracking-wide transition-colors ${
              isExpired
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : sizeMissing
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : added
                ? "bg-emerald-600 text-white"
                : "bg-[#1A1A1A] text-white active:scale-[0.98]"
            }`}
            data-testid="sheet-add-to-cart"
          >
            {isExpired ? (
              "Акция завершена"
            ) : sizeMissing ? (
              "Выберите размер"
            ) : added ? (
              <>
                <Check size={16} strokeWidth={2.5} /> Добавлено в корзину
              </>
            ) : (
              "Добавить в корзину"
            )}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default ProductBottomSheet;
